import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Check } from 'lucide-react';
import { formatDate, formatDateTime, formatEuro, formatSquareMeters } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Checkbox, Eyebrow, ImageSlot, Skeleton, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { SignatureCanvas } from '../SignatureCanvas';
import { PublicFrame } from './PublicFrame';
import {
  goneOf,
  isNotFound,
  usePublicReservation,
  usePublicReservationMutations,
  type PublicReservation,
} from './api';
import styles from './ReservationPublicPage.module.css';

type Step = 1 | 2 | 3;

const STEPS: ReadonlyArray<{ step: Step; label: string }> = [
  { step: 1, label: 'Daten prüfen' },
  { step: 2, label: 'Unterschreiben' },
  { step: 3, label: 'Fertig' },
];

function StepIndicator({ current }: { current: Step }) {
  return (
    <ol className={styles.steps} aria-label="Schritte">
      {STEPS.map((entry, index) => {
        const state = entry.step < current ? 'done' : entry.step === current ? 'current' : 'open';
        return (
          <li
            key={entry.step}
            className={styles.stepItem}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            {index > 0 ? <span className={styles.stepLine} aria-hidden="true" /> : null}
            <span className={styles.step}>
              <span className={clsx(styles.stepCircle, styles[state])} aria-hidden="true">
                {state === 'done' ? <Check size={11} strokeWidth={2.5} /> : entry.step}
              </span>
              <span className={clsx(styles.stepLabel, styles[`label_${state}`])}>{entry.label}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{value}</span>
    </div>
  );
}

/** `/r/:token` — Reservierung 04–07: check the data, sign, done, abort; 404 and 410 pages. */
export function ReservationPublicPage() {
  const { token = '' } = useParams();
  const query = usePublicReservation(token);
  const data = query.data;

  if (query.isPending) {
    return (
      <PublicFrame context="Reservierung">
        <div className={styles.skeleton} aria-busy="true">
          <Skeleton width="45%" height={20} />
          <Skeleton width="70%" height={28} />
          <Skeleton height={210} />
          <Skeleton height={160} />
        </div>
      </PublicFrame>
    );
  }

  if (query.isError) {
    const gone = goneOf(query.error);
    if (gone) {
      const expired = gone.state === 'expired';
      return (
        <PublicFrame context="Reservierung" width={620} advisor={gone.advisor}>
          <div className={styles.resultCard}>
            <Eyebrow as="div">Link abgelaufen</Eyebrow>
            <h1 className={styles.resultTitle}>{gone.message}</h1>
            <p className={styles.resultText}>
              {expired
                ? 'Aus Sicherheitsgründen gilt ein Link 30 Tage. Ihr Berater sendet Ihnen gern einen neuen Link.'
                : 'Die Reservierung wurde inzwischen beendet. Bei Fragen wenden Sie sich bitte an Ihren Berater.'}
            </p>
            {gone.advisor ? (
              <div className={styles.advisorLine}>
                Oder rufen Sie {gone.advisor.name} an
                {gone.advisor.phone ? (
                  <>
                    : <a href={`tel:${gone.advisor.phone.replace(/\s+/g, '')}`}>{gone.advisor.phone}</a>
                  </>
                ) : gone.advisor.email ? (
                  <>
                    {' '}
                    oder schreiben Sie an <a href={`mailto:${gone.advisor.email}`}>{gone.advisor.email}</a>
                  </>
                ) : (
                  '.'
                )}
              </div>
            ) : null}
          </div>
        </PublicFrame>
      );
    }
    return (
      <PublicFrame context="Reservierung" width={620}>
        <div className={styles.resultCard}>
          <Eyebrow as="div">{isNotFound(query.error) ? 'Link nicht gefunden' : 'Fehler'}</Eyebrow>
          <h1 className={styles.resultTitle}>
            {isNotFound(query.error) ? 'Dieser Link wurde nicht gefunden.' : errorMessage(query.error)}
          </h1>
          <p className={styles.resultText}>
            {isNotFound(query.error)
              ? 'Bitte prüfen Sie den Link aus Ihrer E-Mail oder WhatsApp-Nachricht. Bei Fragen hilft Ihnen Ihr Berater weiter.'
              : 'Bitte versuchen Sie es in einem Moment erneut.'}
          </p>
          {!isNotFound(query.error) ? (
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Erneut laden
            </Button>
          ) : null}
        </div>
      </PublicFrame>
    );
  }

  if (!data) return null;
  return <Flow token={token} data={data} />;
}

function Flow({ token, data }: { token: string; data: PublicReservation }) {
  const { sign, abort, resume } = usePublicReservationMutations(token);
  const [step, setStep] = useState<Step>(data.state === 'signed' ? 3 : 1);
  const [consent, setConsent] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [place, setPlace] = useState('');
  const [errors, setErrors] = useState<{
    consent?: string | undefined;
    signature?: string | undefined;
    place?: string | undefined;
  }>({});

  useEffect(() => {
    if (data.state === 'signed') setStep(3);
  }, [data.state]);

  const unit = data.unit;
  const context = 'Reservierung';
  const advisor = { name: data.advisor.name, email: data.advisor.email, phone: data.advisor.phone };
  const legal = { imprintUrl: data.organization.imprintUrl, privacyUrl: data.organization.privacyUrl };
  const frameProps = { context, advisor, legal, organizationName: data.organization.name };
  const serverFields = sign.error instanceof ApiError ? sign.error.fields : {};
  const genericSignError =
    sign.isError && Object.keys(serverFields).length === 0 ? errorMessage(sign.error) : null;

  function submitSignature() {
    const next: typeof errors = {};
    if (!consent)
      next.consent = 'Bitte bestätigen Sie, dass Sie die Reservierungsvereinbarung gelesen haben.';
    if (signature === null) next.signature = 'Bitte unterschreiben Sie mit Finger oder Maus.';
    if (place.trim() === '') next.place = 'Bitte geben Sie den Ort an.';
    setErrors(next);
    if (Object.keys(next).length > 0 || signature === null) return;
    sign.mutate(
      { signaturePng: signature, place: place.trim(), consent: true },
      { onSuccess: () => setStep(3) },
    );
  }

  // --- 07 aborted ---------------------------------------------------------------------------------
  if (data.state === 'aborted') {
    const phone = data.advisor.phone;
    return (
      <PublicFrame {...frameProps}>
        <div className={styles.resultCard}>
          <Eyebrow as="div">Reservierung abgebrochen</Eyebrow>
          <h1 className={styles.resultTitle}>Sie haben die Reservierung nicht abgeschlossen.</h1>
          <p className={styles.resultText}>
            {unit.number} bleibt vorerst für Sie zurückgehalten. {data.advisor.name} erfährt davon und meldet
            sich bei Ihnen. Sie können auch jetzt fortfahren.
          </p>
          {resume.isError ? <Banner tone="danger">{errorMessage(resume.error)}</Banner> : null}
          {abort.isError ? <Banner tone="danger">{errorMessage(abort.error)}</Banner> : null}
          <div className={styles.buttonRow}>
            <Button
              loading={resume.isPending}
              onClick={() => resume.mutate(undefined, { onSuccess: () => setStep(2) })}
            >
              Doch reservieren
            </Button>
            {phone ? (
              <a href={`tel:${phone.replace(/\s+/g, '')}`} className={styles.secondaryLink}>
                {data.advisor.name} anrufen · {phone}
              </a>
            ) : (
              <a href={`mailto:${data.advisor.email}`} className={styles.secondaryLink}>
                {data.advisor.name} schreiben
              </a>
            )}
          </div>
          <div className={styles.reasonBlock}>
            <div className={styles.reasonLabel}>Möchten Sie uns einen Grund nennen? (optional)</div>
            <div className={styles.pills} role="group" aria-label="Grund">
              {data.abortReasons.map((reason) => {
                const active = data.abortReason === reason;
                return (
                  <button
                    key={reason}
                    type="button"
                    aria-pressed={active}
                    disabled={abort.isPending}
                    className={clsx(styles.pill, active && styles.pillActive)}
                    onClick={() => abort.mutate(active ? null : reason)}
                  >
                    {reason}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </PublicFrame>
    );
  }

  // --- 06 done ------------------------------------------------------------------------------------
  if (data.state === 'signed' || step === 3) {
    return (
      <PublicFrame {...frameProps}>
        <StepIndicator current={3} />
        <div className={clsx(styles.resultCard, styles.doneCard)}>
          <div className={styles.doneHead}>
            <span className={styles.doneCheck} aria-hidden="true">
              <Check size={11} strokeWidth={2.5} />
            </span>
            <Eyebrow as="div">
              Unterschrieben{data.signedAt ? ` · ${formatDateTime(data.signedAt).replace(' · ', ', ')}` : ''}
            </Eyebrow>
          </div>
          <h1 className={styles.resultTitle}>{unit.number} ist für Sie reserviert.</h1>
          <p className={styles.resultText}>
            Die Wohnung wird ab sofort niemand anderem angeboten. {data.advisor.name} meldet sich wegen der
            nächsten Schritte zur Finanzierung.
          </p>
          <div className={styles.rows}>
            <Row label="Wohnung" value={`${unit.number} · ${unit.propertyName}`} />
            <Row label="Kaufpreis" value={unit.priceCents !== null ? formatEuro(unit.priceCents) : '—'} />
            <Row
              label="Bestätigung"
              value={data.buyer.email ? `per E-Mail an ${data.buyer.email}` : 'per E-Mail'}
            />
          </div>
          <div className={styles.buttonRow}>
            <a href={data.agreementPdfUrl} target="_blank" rel="noreferrer" className={styles.secondaryLink}>
              Vereinbarung als PDF
            </a>
          </div>
        </div>
      </PublicFrame>
    );
  }

  // --- 05 sign ------------------------------------------------------------------------------------
  if (step === 2) {
    return (
      <PublicFrame {...frameProps}>
        <StepIndicator current={2} />
        <h1 className={styles.title}>Reservierung unterschreiben</h1>
        <div className={styles.card}>
          {genericSignError ? <Banner tone="danger">{genericSignError}</Banner> : null}
          <p className={styles.agreementText}>{data.agreementText}</p>
          <div className={styles.inlineLink}>
            <a href={data.agreementPdfUrl} target="_blank" rel="noreferrer" className={styles.textLink}>
              Vollständige Vereinbarung lesen
            </a>
          </div>
          <div className={styles.consent}>
            <Checkbox
              label="Ich habe die Reservierungsvereinbarung gelesen. *"
              checked={consent}
              onChange={(event) => {
                setConsent(event.target.checked);
                setErrors((current) => ({ ...current, consent: undefined }));
              }}
            />
            {(errors.consent ?? serverFields['consent']) ? (
              <div className={styles.fieldError} role="alert">
                {errors.consent ?? serverFields['consent']}
              </div>
            ) : null}
          </div>
          <div className={styles.fieldLabel}>
            Unterschrift <span className={styles.required}>*</span>
          </div>
          <SignatureCanvas
            label="Unterschrift"
            onChange={(value) => {
              setSignature(value);
              if (value) setErrors((current) => ({ ...current, signature: undefined }));
            }}
            error={errors.signature ?? serverFields['signaturePng']}
            hint={`${place.trim() || 'Ort'}, ${formatDate(new Date())} · mit Finger oder Maus unterschreiben`}
          />
          <TextField
            label="Ort"
            required
            value={place}
            onChange={(event) => {
              setPlace(event.target.value);
              setErrors((current) => ({ ...current, place: undefined }));
            }}
            error={errors.place ?? serverFields['place']}
            autoComplete="address-level2"
            className={styles.placeField}
          />
        </div>
        <div className={styles.footerRow}>
          <Button variant="secondary" onClick={() => setStep(1)} disabled={sign.isPending}>
            Zurück
          </Button>
          <button
            type="button"
            className={styles.textLink}
            disabled={abort.isPending || sign.isPending}
            onClick={() => abort.mutate(null)}
          >
            Doch nicht reservieren
          </button>
          <span className={styles.spacer} />
          <Button loading={sign.isPending} onClick={submitSignature}>
            Unterschreiben und reservieren
          </Button>
        </div>
        {abort.isError ? <Banner tone="danger">{errorMessage(abort.error)}</Banner> : null}
      </PublicFrame>
    );
  }

  // --- 04 check the data --------------------------------------------------------------------------
  const location = [unit.address, unit.building, unit.floor].filter(Boolean).join(' · ');
  return (
    <PublicFrame {...frameProps}>
      <StepIndicator current={1} />
      <div>
        <h1 className={styles.title}>Guten Tag {data.buyer.name},</h1>
        <p className={styles.lead}>
          bitte prüfen Sie die Angaben zu Ihrer Reservierung. Mit der Unterschrift wird die Wohnung für Sie
          zurückgehalten, bis Sie sich entscheiden.
        </p>
      </div>
      <div className={styles.objectCard}>
        <ImageSlot
          alt={`Objektbild · ${unit.propertyName}`}
          placeholder={`Objektbild · ${unit.propertyName}`}
          radius="none"
          className={styles.objectImage}
        />
        <div className={styles.objectBody}>
          <Eyebrow as="div">Ihre Wohnung</Eyebrow>
          <div className={styles.objectTitle}>
            {unit.number} · {unit.propertyName}
          </div>
          {location ? <div className={styles.objectLocation}>{location}</div> : null}
          <div className={styles.metrics}>
            <div className={styles.metric}>
              <Eyebrow as="div">Wohnfläche</Eyebrow>
              <div className={styles.metricValue}>
                {unit.areaSqm !== null ? formatSquareMeters(unit.areaSqm) : '—'}
              </div>
            </div>
            <div className={styles.metric}>
              <Eyebrow as="div">Zimmer</Eyebrow>
              <div className={styles.metricValue}>{unit.rooms ?? '—'}</div>
            </div>
            <div className={styles.metric}>
              <Eyebrow as="div">Kaufpreis</Eyebrow>
              <div className={styles.metricValue}>
                {unit.priceCents !== null ? formatEuro(unit.priceCents) : '—'}
              </div>
            </div>
            {unit.parkingSpace ? (
              <div className={styles.metric}>
                <Eyebrow as="div">Stellplatz</Eyebrow>
                <div className={styles.metricValue}>
                  {unit.parkingSpace.number}
                  {unit.parkingSpace.priceCents !== null
                    ? ` · ${formatEuro(unit.parkingSpace.priceCents)}`
                    : ''}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className={styles.card}>
        <div className={styles.dataGrid}>
          <div>
            <Eyebrow as="div" className={styles.groupLabel}>
              Käufer
            </Eyebrow>
            <Row label="Name" value={data.buyer.name} />
            <Row label="Anschrift" value={data.buyer.address ?? '—'} />
            <Row label="E-Mail" value={data.buyer.email ?? '—'} />
          </div>
          <div>
            <Eyebrow as="div" className={styles.groupLabel}>
              Reservierung
            </Eyebrow>
            <Row label="Verkäufer" value={data.seller ?? data.organization.name} />
            <Row label="Laufzeit" value="bis Sie widerrufen" />
            <Row label="Kosten" value="keine" />
          </div>
        </div>
        <div className={styles.inlineNote}>
          <span className={styles.noteText}>Stimmt etwas nicht?</span>
          <a
            href={`mailto:${data.advisor.email}?subject=${encodeURIComponent(`Reservierung ${unit.number} · ${unit.propertyName}`)}`}
            className={styles.textLink}
          >
            {data.advisor.name} Bescheid geben
          </a>
        </div>
      </div>
      <div className={styles.footerRow}>
        <span className={styles.footnote}>
          Reservierungsvereinbarung als PDF, {data.agreementPages}{' '}
          {data.agreementPages === 1 ? 'Seite' : 'Seiten'}
        </span>
        <a href={data.agreementPdfUrl} target="_blank" rel="noreferrer" className={styles.textLink}>
          Vorab ansehen
        </a>
        <span className={styles.spacer} />
        <Button onClick={() => setStep(2)}>Angaben stimmen, weiter</Button>
      </div>
    </PublicFrame>
  );
}
